import { NgbTooltipConfigService } from '@grid-ui/common';
import { environment } from '@grid-ui/environment';
import { FeatureFlagService } from '@grid-ui/feature-flag';
import { IpLoginService } from '@grid-ui/ip-login';
import { selectLoggedIn } from '@grid-ui/whoami';
import { LoggingService } from '@maplecroft-ui/logging';
import { MapService } from '@maplecroft-ui/maps';
import { TranslateService } from '@maplecroft-ui/translate';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import { filter, switchMap, take, tap } from 'rxjs/operators';

export function appInitializerFactory(
  loggingService: LoggingService,
  ipLoginService: IpLoginService,
  mapService: MapService,
  featureFlagService: FeatureFlagService,
  tooltipConfigService: NgbTooltipConfigService,
  store: Store,
  doc: Document,
  // Required to ensure translate service is initialised and translations are preloaded
  _translateService: TranslateService,
): () => Promise<any> {
  return async () => {
    await loggingService.initialise({
      dsn: environment.appConfig.sentryDsn,
      release: '__mcDeployToken_sentryRelease__',
      environment: environment.environment,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      tracesSampleRate: 0.2,
      tracePropagationTargets: ['localhost', /^https:\/\/((?!maps)(.*))\.maplecroft\.com/],
    });

    await ipLoginService.getIpAuthToken();

    tooltipConfigService.initialise();

    // Authenticate the maps service early once logged in
    void firstValueFrom(
      store.select(selectLoggedIn).pipe(
        filter((loggedIn) => loggedIn),
        take(1),
        tap(() => {
          // Wait for authentication to complete before removing duplicate CSRF cookies, this is so we can log the organisation/user in sentry
          removeDuplicateCSRFCookie(doc, loggingService);
        }),
        switchMap(() => mapService.authenticate()),
      ),
    );

    await featureFlagService.initialise().catch((error) => {
      console.error('An error occurred during the Angular App initialization process: ', error);
      throw error;
    });
  };
}

function removeDuplicateCSRFCookie(doc: Document, loggingService: LoggingService): void {
  if (findCSRFCookies(doc).length > 1) {
    void loggingService.log('Multiple CSRF cookies found, removing duplicate', { level: 'debug' });

    doc.cookie = `csrftoken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;

    if (findCSRFCookies(doc).length === 1) {
      void loggingService.log('Duplicate CSRF cookie removed successfully', { level: 'debug' });
    } else {
      void loggingService.log('Failed to remove duplicate CSRF cookie', { level: 'error' });
    }
  }
}

function findCSRFCookies(doc: Document): string[] {
  return doc.cookie.split('; ').filter((cookie) => cookie.startsWith('csrftoken='));
}
